import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import DatePicker from "../../components/DatePicker";
// Shared encoder shell (header, stats grid, sticky save, skeleton, toast)
import g from "../grades/Grades.module.css";
// Page-specific (date bar, status pills, quick action)
import a from "./TeacherAttendancePage.module.css";

/** PRESENT is the default "happy path"; ABSENT/LATE/EXCUSED are the
 *  exceptions a teacher marks individually. Order also drives the
 *  segmented-control button order so the most-used button sits leftmost. */
const STATUSES = [
  { code: "PRESENT", label: "P", title: "Present", activeClass: "statusActivePresent" },
  { code: "LATE",    label: "L", title: "Late",    activeClass: "statusActiveLate"    },
  { code: "EXCUSED", label: "E", title: "Excused", activeClass: "statusActiveExcused" },
  { code: "ABSENT",  label: "A", title: "Absent",  activeClass: "statusActiveAbsent"  },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.code, s.title]));

/* ── Avatar helpers (mirror grade page) ────────────────────────────── */
const AVATAR_COLORS = ["#5a4e3a", "#3a4e4a", "#4a3a5a", "#4e4a3a", "#3a4a5a", "#5a3a3a", "#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(first, last) {
  const x = (first || "").trim()[0] || "";
  const y = (last  || "").trim()[0] || "";
  return (x + y).toUpperCase() || "?";
}

/** "2026-05-08" — local-time YYYY-MM-DD without UTC roll-over. */
function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
/** Shift an ISO date by N days (negative for past). Pure string math —
 *  no Date constructor so DST never silently mutates the result. */
function shiftIso(iso, deltaDays) {
  const [y, m, d] = iso.split("-").map(Number);
  // Use UTC arithmetic, then format back to local-style YYYY-MM-DD. Safe
  // because we only care about the calendar date, not a timezone-aware
  // instant.
  const t = Date.UTC(y, m - 1, d) + deltaDays * 86400000;
  const dt = new Date(t);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

export default function TeacherAttendancePage() {
  const { id } = useParams();
  const classOfferingId = Number(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  const [offering, setOffering] = useState(null);
  const [subject,  setSubject]  = useState(null);
  const [section,  setSection]  = useState(null);
  const [semester, setSemester] = useState(null);
  const [students, setStudents] = useState([]);

  const [sessionDate, setSessionDate] = useState(isoToday());

  // records[studentId] = { status, remarks }   (current edits)
  const [records, setRecords] = useState({});
  // Snapshot at last load — drives dirty highlighting + sticky bar.
  const [original, setOriginal] = useState({});

  // Toast — same idiom as the grade encoder.
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  function showToast(kind, text) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ kind, text });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  /* ── Initial load: offering metadata + students (once per class) ──── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const off = await fetch(`${API_BASE}/api/class-offerings`).then(r => r.json());
        const me = off.find(c => c.id === classOfferingId);
        if (!me) throw new Error("Class offering not found");

        const [subjects, sections, semesters, allStudents] = await Promise.all([
          fetch(`${API_BASE}/api/subjects`).then(r => r.json()),
          fetch(`${API_BASE}/api/sections`).then(r => r.json()),
          fetch(`${API_BASE}/api/semesters`).then(r => r.json()),
          fetch(`${API_BASE}/api/students?sectionId=${me.sectionId}`).then(r => r.json()),
        ]);
        if (cancelled) return;

        setOffering(me);
        setSubject(subjects.find(x => x.id === me.subjectId) || null);
        setSection(sections.find(x => x.id === me.sectionId) || null);
        setSemester(semesters.find(x => x.id === me.semesterId) || null);
        setStudents(allStudents);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classOfferingId]);

  /* ── Per-date load: fetch existing attendance for the picked day ── */
  useEffect(() => {
    if (!classOfferingId || !sessionDate) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetch(
          `${API_BASE}/api/attendance?classOfferingId=${classOfferingId}&date=${sessionDate}`
        ).then(r => r.ok ? r.json() : []);
        if (cancelled) return;
        const r = {};
        rows.forEach(row => {
          r[row.studentId] = {
            status: row.status || "",
            remarks: row.remarks || "",
          };
        });
        setRecords(r);
        setOriginal(JSON.parse(JSON.stringify(r)));
      } catch {
        if (!cancelled) { setRecords({}); setOriginal({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [classOfferingId, sessionDate]);

  /* ── Mutation helpers ──────────────────────────────────────────── */
  function setStatus(studentId, status) {
    setRecords(prev => {
      const cur = prev[studentId] || { status: "", remarks: "" };
      // Toggle — clicking the active button clears the row (back to unset).
      const next = cur.status === status ? "" : status;
      return { ...prev, [studentId]: { ...cur, status: next } };
    });
  }
  function setRemarks(studentId, value) {
    setRecords(prev => {
      const cur = prev[studentId] || { status: "", remarks: "" };
      return { ...prev, [studentId]: { ...cur, remarks: value } };
    });
  }
  function markAllPresent() {
    setRecords(prev => {
      const next = { ...prev };
      students.forEach(st => {
        const cur = prev[st.id] || { status: "", remarks: "" };
        // Only flip rows that aren't already explicitly marked — preserves
        // pre-existing ABSENT / LATE / EXCUSED entries the teacher set.
        if (!cur.status) next[st.id] = { ...cur, status: "PRESENT" };
      });
      return next;
    });
  }
  function clearAll() {
    // Set every row's status to "" (but keep remarks). On save the server
    // deletes any rows where both status and remarks end up blank.
    setRecords(prev => {
      const next = { ...prev };
      students.forEach(st => {
        const cur = prev[st.id] || { status: "", remarks: "" };
        next[st.id] = { ...cur, status: "" };
      });
      return next;
    });
  }

  /* ── Dirty tracking ────────────────────────────────────────────── */
  function isRowDirty(studentId) {
    const cur  = records[studentId]   || { status: "", remarks: "" };
    const orig = original[studentId]  || { status: "", remarks: "" };
    return cur.status !== orig.status || cur.remarks !== orig.remarks;
  }
  const dirtyCount = useMemo(() => {
    let n = 0;
    const ids = new Set([...Object.keys(records), ...Object.keys(original)]);
    for (const id of ids) if (isRowDirty(Number(id))) n++;
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, original]);

  /* ── Live counts for the stats strip ───────────────────────────── */
  const stats = useMemo(() => {
    let p = 0, a = 0, l = 0, e = 0, unset = 0;
    students.forEach(st => {
      const r = records[st.id];
      switch (r?.status) {
        case "PRESENT": p++; break;
        case "ABSENT":  a++; break;
        case "LATE":    l++; break;
        case "EXCUSED": e++; break;
        default: unset++; break;
      }
    });
    return { total: students.length, p, a, l, e, unset };
  }, [students, records]);

  /* ── Save ──────────────────────────────────────────────────────── */
  async function saveAll() {
    setBusy(true);
    try {
      const entries = students.map(st => ({
        studentId: st.id,
        status:    records[st.id]?.status  || "",
        remarks:   records[st.id]?.remarks || "",
      }));
      const res = await fetch(`${API_BASE}/api/attendance/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ classOfferingId, sessionDate, entries }),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      const result = await res.json();
      // Refresh snapshot so dirty state collapses without re-fetching.
      setOriginal(JSON.parse(JSON.stringify(records)));
      showToast(
        "success",
        `Saved ${result.saved} record${result.saved === 1 ? "" : "s"}` +
        (result.deleted ? ` · removed ${result.deleted}` : "")
      );
    } catch (e) {
      showToast("error", e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const headingTitle = subject?.title || (offering ? `Subject #${offering.subjectId}` : "Class");
  const isToday = sessionDate === isoToday();

  return (
    <div className={g.page}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className={g.headerUpgraded}>
        <button className={g.backBtnPremium} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}>
          ← Back
        </button>
        <div>
          <div className={g.eyebrow}>Teacher · Attendance</div>
          <h1 className={g.title} style={{ marginBottom: 2 }}>
            {loading ? "Loading…" : headingTitle}
          </h1>
          {!loading && (subject?.code || section?.name || semester?.label) && (
            <div className={g.headerMetaRow}>
              {subject?.code && <span className={`${g.headerPill} ${g.headerPillCode}`}>{subject.code}</span>}
              {section?.name  && <span className={g.headerPill}>Section {section.name}</span>}
              {semester?.label && <span className={g.headerPill}>{semester.label} sem</span>}
            </div>
          )}
        </div>
      </div>

      <div className={g.card}>
        {error && <div className={g.error}>{error}</div>}

        {/* ── Date bar: "Recording for [date] · ← yesterday · today · → tomorrow" ── */}
        <div className={a.dateBar}>
          <span className={a.dateBarLabel}>Recording for</span>
          <div className={a.dateBarPicker}>
            <DatePicker value={sessionDate} onChange={setSessionDate} />
          </div>
          <div className={a.dateBarShortcuts}>
            <button type="button" className={a.dateShortcut}
                    onClick={() => setSessionDate(shiftIso(sessionDate, -1))}>
              ← Previous day
            </button>
            <button type="button"
                    className={`${a.dateShortcut} ${isToday ? a.dateShortcutPrimary : ""}`}
                    onClick={() => setSessionDate(isoToday())}
                    disabled={isToday}>
              Today
            </button>
            <button type="button" className={a.dateShortcut}
                    onClick={() => setSessionDate(shiftIso(sessionDate, +1))}>
              Next day →
            </button>
          </div>
        </div>

        {/* ── Loading skeleton ────────────────────────────────────── */}
        {loading && (
          <div className={g.skeleton}>
            <div className={g.skeletonRow} />
            <div className={g.skeletonRow} />
            <div className={g.skeletonRow} />
            <div className={g.skeletonRow} />
          </div>
        )}

        {/* ── Empty roster ────────────────────────────────────────── */}
        {!loading && students.length === 0 && (
          <div className={g.emptyPremium}>
            <div className={g.emptyPremiumTitle}>No students in this section yet.</div>
            <div className={g.emptyPremiumSub}>
              Enroll students from <strong>Directory → Enrollments</strong>; they'll appear here.
            </div>
          </div>
        )}

        {/* ── Roster encoder ──────────────────────────────────────── */}
        {!loading && students.length > 0 && (
          <>
            {/* Stats strip — live counts color-tinted by status */}
            <div className={g.statsStrip}>
              <div className={g.statTile}>
                <div className={g.statTileLabel}>Roster</div>
                <div className={g.statTileValue}>{stats.total}</div>
                <div className={g.statTileSub}>{prettyDate(sessionDate)}</div>
              </div>
              <div className={`${g.statTile} ${a.statTilePresent}`}>
                <div className={g.statTileLabel}>Present</div>
                <div className={g.statTileValue}>{stats.p}</div>
                <div className={g.statTileSub}>{stats.unset > 0 ? `${stats.unset} unmarked` : "all marked"}</div>
              </div>
              <div className={`${g.statTile} ${a.statTileLate}`}>
                <div className={g.statTileLabel}>Late</div>
                <div className={g.statTileValue}>{stats.l}</div>
                <div className={g.statTileSub}>arriving past start</div>
              </div>
              <div className={`${g.statTile} ${a.statTileExcused}`}>
                <div className={g.statTileLabel}>Excused</div>
                <div className={g.statTileValue}>{stats.e}</div>
                <div className={g.statTileSub}>with note on file</div>
              </div>
              <div className={`${g.statTile} ${a.statTileAbsent}`}>
                <div className={g.statTileLabel}>Absent</div>
                <div className={g.statTileValue}>{stats.a}</div>
                <div className={g.statTileSub}>no note today</div>
              </div>
            </div>

            {/* Quick actions row */}
            <div className={a.quickBar}>
              <button type="button" className={a.quickAction} onClick={markAllPresent}>
                ✓ Mark all present
              </button>
              <button type="button" className={`${a.quickAction} ${a.quickActionMuted}`} onClick={clearAll}>
                Clear statuses
              </button>
              <span className={a.quickHint}>
                Tap a status to set; tap it again to clear that row.
              </span>
            </div>

            {/* Roster table */}
            <div className={g.tableWrap}>
              <table className={`${g.table} ${g.tableUpgraded}`}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((st, idx) => {
                    const cur = records[st.id] || { status: "", remarks: "" };
                    const dirty = isRowDirty(st.id);
                    const fullName = `${st.firstName || ""} ${st.lastName || ""}`.trim();
                    return (
                      <tr
                        key={st.id}
                        className={dirty ? g.rowDirty : ""}
                        style={{ animationDelay: `${Math.min(idx, 12) * 25}ms` }}
                      >
                        <td>
                          <div className={g.studentCell}>
                            <span className={g.studentAvatar} style={{ background: avatarColor(fullName) }}>
                              {st.photoUrl
                                ? <img src={st.photoUrl} alt="" className={g.studentAvatarImg} />
                                : initials(st.firstName, st.lastName)}
                            </span>
                            <div>
                              <div className={g.studentName}>{st.lastName}, {st.firstName}</div>
                              <div className={g.studentMeta}>#{st.studentNumber}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div
                            className={a.statusGroup}
                            role="radiogroup"
                            aria-label={`Attendance status for ${fullName}`}
                          >
                            {STATUSES.map(stat => {
                              const active = cur.status === stat.code;
                              return (
                                <button
                                  key={stat.code}
                                  type="button"
                                  className={`${a.statusBtn} ${active ? a[stat.activeClass] : ""}`}
                                  role="radio"
                                  aria-checked={active}
                                  title={stat.title}
                                  onClick={() => setStatus(st.id, stat.code)}
                                >
                                  {stat.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                        <td className={g.remarksCell}>
                          <input
                            type="text"
                            className={g.inputRemarksPremium}
                            placeholder="Optional note…"
                            value={cur.remarks}
                            aria-label={`Attendance remarks for ${fullName}`}
                            onChange={e => setRemarks(st.id, e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Sticky save bar — only while dirty */}
            {dirtyCount > 0 && (
              <div className={g.stickyBar} role="region" aria-label="Unsaved changes">
                <div>
                  <div className={g.stickyBarLabel}>
                    {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"} for {prettyDate(sessionDate)}
                  </div>
                  <span className={g.stickyBarHint}>
                    Save to commit · changes auto-clear after a successful save
                  </span>
                </div>
                <button
                  type="button"
                  className={g.btnPrimaryPremium}
                  onClick={saveAll}
                  disabled={busy}
                >
                  {busy ? "Saving…" : "Save attendance"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Toast — keyed so a second save re-triggers the animation */}
      {toast && (
        <div
          key={`${toast.kind}-${toast.text}`}
          role="status"
          className={`${g.toast} ${toast.kind === "error" ? g.toastError : g.toastSuccess}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

/** Exported so the dashboard's status badge can render the same way the
 *  encoder does without duplicating the label table. */
export { STATUS_LABEL };
